import { useCallback, useEffect, useRef, useState } from 'react';

import { NODE_TYPES } from '@/shared/api/openmetadata';
import type { OpenMetadataSource } from '@/shared/api/openmetadata';
import { ASSET_TYPES } from '@/shared/api/openmetadata';
import { EXPERIMENT_ASSET_TYPES_FULL } from '@/shared/lib/constants';
import type { AssetTreeNode } from '@/shared/lib/tree';
import { ENTITY_TYPE_CATEGORY_MAP, findNodeByKey } from '@/shared/lib/tree';

import {
  EXPERIMENT_RELATED_TYPES,
  isExcludedFromDiscoveryAssetTypeFilter,
  RUN_RELATED_TYPES,
} from '@/entities/asset-search/domain/constants';
import { useFilterStore } from '@/entities/discovery';
import type { QuickFilter } from '@/entities/discovery/domain/types';
import { useSearchStore } from '@/entities/search';

type UseAssetTreeSelectionProps = {
  treeData: AssetTreeNode[];
};

export const useAssetTreeSelection = ({ treeData }: UseAssetTreeSelectionProps) => {
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const lastChangeSource = useFilterStore((state) => state.lastChangeSource);
  const lastChangeSourceRef = useRef<string | undefined>();

  useEffect(() => {
    if (
      (lastChangeSource === 'sidebar' || lastChangeSource === 'topBar') &&
      lastChangeSource !== lastChangeSourceRef.current
    ) {
      setSelectedKeys([]);
    }

    lastChangeSourceRef.current = lastChangeSource;
  }, [lastChangeSource]);

  const onSelect = useCallback(
    (newSelectedKeys: React.Key[], info: { node: { key: React.Key } }) => {
      if (newSelectedKeys.length === 0) return;

      const nodeKey = String(info.node.key);
      const targetNode = findNodeByKey(treeData, nodeKey);

      if (!targetNode) return;

      if (targetNode.disabled) return;

      const filterStore = useFilterStore.getState();
      const searchStore = useSearchStore.getState();

      if (targetNode.type === 'project') {
        filterStore.clearAllFilters();
        const projectData = targetNode.data as { projectId?: string };
        const projectId = projectData.projectId || String(targetNode.key).replace('project-', '');

        const quickFilter: QuickFilter = {
          projectId,
        };

        setSelectedKeys([targetNode.key]);
        filterStore.updateAssetTypeFilter([...EXPERIMENT_ASSET_TYPES_FULL], 'tree');
        filterStore.updateQuickFilter(quickFilter, 'tree');
        searchStore.setQuery('');
        return;
      }

      if (targetNode.type === 'experiment') {
        filterStore.clearAllFilters();
        const experimentData = targetNode.data as { experimentId?: string };
        const experimentId = experimentData.experimentId || String(targetNode.key).replace('project-exp-', '');

        const quickFilter: QuickFilter = {
          experimentId,
        };

        setSelectedKeys([targetNode.key]);
        filterStore.updateAssetTypeFilter(
          [...EXPERIMENT_RELATED_TYPES]
            .filter((t) => t !== 'project')
            .filter((t) => !isExcludedFromDiscoveryAssetTypeFilter(t)),
          'tree'
        );
        filterStore.updateQuickFilter(quickFilter, 'tree');
        searchStore.setQuery('');
        return;
      }

      if (targetNode.type === 'run') {
        filterStore.clearAllFilters();
        const runData = targetNode.data as { runId?: string };
        const runId = runData.runId || String(targetNode.key).replace('run-', '');

        const quickFilter: QuickFilter = {
          runId,
        };

        setSelectedKeys([targetNode.key]);
        filterStore.updateAssetTypeFilter(
          [...RUN_RELATED_TYPES].filter((t) => !isExcludedFromDiscoveryAssetTypeFilter(t)),
          'tree'
        );
        filterStore.updateQuickFilter(quickFilter, 'tree');
        searchStore.setQuery('');
        return;
      }

      if (['feature_set', 'dataset', 'artifact'].includes(targetNode.type)) {
        filterStore.clearAllFilters();
        const assetData = targetNode.data as { runId?: string };
        const runId = assetData.runId || '';

        const quickFilter: QuickFilter = {
          runId,
        };

        setSelectedKeys([targetNode.key]);
        filterStore.updateAssetTypeFilter([targetNode.type], 'tree');
        filterStore.updateQuickFilter(quickFilter, 'tree');
        searchStore.setQuery('');
        return;
      }

      if (targetNode.type === ASSET_TYPES.EXPERIMENT) {
        filterStore.clearAllFilters();
        setSelectedKeys([targetNode.key]);
        filterStore.updateAssetTypeFilter(['run'], 'tree');
        searchStore.setQuery(String(targetNode.title));
        return;
      }

      if (targetNode.type === NODE_TYPES.ENTITY_TYPE) {
        filterStore.clearAllFilters();
        searchStore.setQuery('');

        const nodeData = targetNode.data as { entityType?: string };
        const entityType = nodeData.entityType;

        if (entityType === 'experiment') {
          setSelectedKeys([targetNode.key]);
          filterStore.updateAssetTypeFilter([...EXPERIMENT_ASSET_TYPES_FULL], 'tree');
          return;
        }

        if (entityType && ENTITY_TYPE_CATEGORY_MAP[entityType]) {
          const assetTypes = ENTITY_TYPE_CATEGORY_MAP[entityType].filter(
            (t) => !isExcludedFromDiscoveryAssetTypeFilter(t)
          );
          setSelectedKeys([targetNode.key]);
          filterStore.updateAssetTypeFilter(assetTypes, 'tree');
        }
        return;
      }

      filterStore.clearAllFilters();
      searchStore.setQuery('');
      setSelectedKeys([targetNode.key]);

      const quickFilter: QuickFilter = {};

      if (targetNode.type === NODE_TYPES.SERVICE_TYPE) {
        const serviceTypeData = targetNode.data as {
          governanceType?: string;
          count?: number;
        };

        if (serviceTypeData.governanceType) {
          setSelectedKeys([targetNode.key]);
          filterStore.updateAssetTypeFilter([serviceTypeData.governanceType], 'tree');
          return;
        }

        if (targetNode.serviceType) {
          quickFilter.serviceType = targetNode.serviceType.toLowerCase();
        }
      }

      if (targetNode.type === NODE_TYPES.SERVICE) {
        const serviceData = targetNode.data as {
          serviceName?: string;
          serviceType?: string;
        };
        if (serviceData.serviceType) {
          quickFilter.serviceType = serviceData.serviceType.toLowerCase();
        }
        if (serviceData.serviceName) {
          quickFilter.serviceName = serviceData.serviceName;
        }
      } else if (targetNode.type === NODE_TYPES.DATABASE) {
        const dbData = targetNode.data as OpenMetadataSource & {
          serviceType?: string;
        };
        if (dbData.serviceType) {
          quickFilter.serviceType = dbData.serviceType.toLowerCase();
        }
        if (dbData.service?.displayName) {
          quickFilter.serviceName = dbData.service.displayName;
        }
        if (dbData.displayName) {
          quickFilter.databaseName = dbData.displayName;
        }
      } else if (targetNode.type === NODE_TYPES.SCHEMA) {
        const schemaData = targetNode.data as OpenMetadataSource & {
          schemaName?: string;
          serviceType?: string;
          serviceName?: string;
          databaseName?: string;
        };
        if (schemaData.serviceType) {
          quickFilter.serviceType = schemaData.serviceType.toLowerCase();
        }
        if (schemaData.serviceName) {
          quickFilter.serviceName = schemaData.serviceName;
        }
        if (schemaData.databaseName) {
          quickFilter.databaseName = schemaData.databaseName;
        }
        if (schemaData.schemaName) {
          quickFilter.schemaName = schemaData.schemaName;
        }
      } else if (targetNode.type === NODE_TYPES.TABLE || targetNode.type === NODE_TYPES.TOPIC) {
        const assetData = targetNode.data as OpenMetadataSource & {
          entityType?: string;
        };

        if (targetNode.serviceType) {
          quickFilter.serviceType = targetNode.serviceType.toLowerCase();
        } else if (assetData.serviceType) {
          quickFilter.serviceType = assetData.serviceType.toLowerCase();
        }

        if (targetNode.serviceName) {
          quickFilter.serviceName = targetNode.serviceName;
        } else if (assetData.service?.displayName) {
          quickFilter.serviceName = assetData.service.displayName;
        }

        if (targetNode.databaseName) {
          quickFilter.databaseName = targetNode.databaseName;
        } else if (assetData.database?.displayName) {
          quickFilter.databaseName = assetData.database.displayName;
        }

        if (targetNode.schemaName) {
          quickFilter.schemaName = targetNode.schemaName;
        } else if (assetData.databaseSchema?.displayName) {
          quickFilter.schemaName = assetData.databaseSchema.displayName;
        }

        if (assetData.entityType) {
          quickFilter.entityType = assetData.entityType;
        } else if (targetNode.type) {
          quickFilter.entityType = targetNode.type;
        }
      }

      const hasQuickFilter = Object.keys(quickFilter).length > 0;
      filterStore.updateQuickFilter(hasQuickFilter ? quickFilter : undefined, 'tree');

      if (hasQuickFilter) {
        if (quickFilter.serviceType || quickFilter.serviceName) {
          const dataSource: string[] = [];
          if (quickFilter.serviceName) {
            dataSource.push(quickFilter.serviceName);
          } else if (quickFilter.serviceType) {
            dataSource.push(quickFilter.serviceType);
          }
          filterStore.updateDataSourceFilter(dataSource, 'tree');
        }

        if (quickFilter.entityType) {
          filterStore.updateAssetTypeFilter([quickFilter.entityType], 'tree');
        }
      }
    },
    [treeData]
  );

  return {
    selectedKeys,
    onSelect,
  };
};
